import { EventTargetInterruptSource } from '@ng-idle/core';

/**
 * Card #364. Two small session behaviours, kept here so the sign-in page, the error
 * interceptor and the trial screen share one definition.
 *
 * 1. "Signed in on another device". When the same person confirms "Log in from this
 *    Device?" elsewhere, the old device's next request is refused with 401 and the backends
 *    (merch-backend, shared-services, quotes) add code SESSION_REPLACED. The interceptor signs
 *    out exactly as before, and also leaves this reason in sessionStorage so the sign-in page
 *    it reloads into can say why. Any other 401 (expired login, idle logout, password reset)
 *    leaves nothing, so the sign-in page stays silent for those, as the card asks.
 *
 * 2. Idle logout counts typing and scrolling, not only clicks: someone writing a long quote
 *    was logged out mid-work. The idle length (1 hour) and the 60-second warning are unchanged.
 */
export const SESSION_REPLACED_CODE = 'SESSION_REPLACED';
export const SIGNOUT_REASON_KEY = 'osmos_signout_reason';
export const SIGNOUT_REASON_OTHER_DEVICE = 'other_device';

/** True when an HTTP error is the backends' "this account signed in on another device" 401. */
export function isSessionReplacedError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const { status, error } = err as { status?: unknown; error?: unknown };
  const code = typeof error === 'object' && error !== null ? (error as { code?: unknown }).code : undefined;
  return (status === 401 || status === 440) && code === SESSION_REPLACED_CODE;
}

/** Leaves the "other device" reason for the sign-in page. Never throws (storage may be blocked). */
export function rememberSignedOutOnOtherDevice(storage: Storage | null = safeSessionStorage()): void {
  try {
    storage?.setItem(SIGNOUT_REASON_KEY, SIGNOUT_REASON_OTHER_DEVICE);
  } catch {
    // Private mode / blocked storage: the sign-out still happens, just without the note.
  }
}

/** Reads AND clears the reason, so the note shows once. True only for "other device". */
export function takeSignedOutOnOtherDevice(storage: Storage | null = safeSessionStorage()): boolean {
  try {
    const reason = storage?.getItem(SIGNOUT_REASON_KEY);
    storage?.removeItem(SIGNOUT_REASON_KEY);
    return reason === SIGNOUT_REASON_OTHER_DEVICE;
  } catch {
    return false;
  }
}

function safeSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** What counts as activity for the idle-logout timer. */
export const IDLE_ACTIVITY_EVENTS = 'click keydown wheel touchstart';

/** Passive: the handler only notes activity, so wheel/touch listeners never delay scrolling. */
const IDLE_LISTENER_OPTIONS = { passive: true, throttleDelay: 500 };

/**
 * Interrupt sources for @ng-idle. Document-level so typing in any field counts; `scroll` is
 * listened for on window because it does not bubble from inner elements (wheel/touch cover
 * those). Each source is throttled to one interrupt per 500 ms, so keystrokes cost nothing.
 */
export function idleActivityInterrupts(doc: Document = document, win: Window = window): EventTargetInterruptSource[] {
  return [
    new EventTargetInterruptSource(doc, IDLE_ACTIVITY_EVENTS, IDLE_LISTENER_OPTIONS),
    new EventTargetInterruptSource(win, 'scroll', IDLE_LISTENER_OPTIONS),
  ];
}
